import React, { useState } from "react";
import Modal from "../../../Shared/Components/Modal/Modal";
import { bulkCreateLearnersAsync } from "../../../Services/AccountService";
import { previewUsersCsvAsync } from "../../../Services/ClientService";
import { showToast } from "../../../Infrastructure/Notifications/ToastrConfig";

const BulkUploadUsers = ({ clientId, groups, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [learners, setLearners] = useState([]);
  const [previewData, setPreviewData] = useState([]);
  const [badRows, setBadRows] = useState([]);
  const [groupId, setGroupId] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (
        selectedFile.type === "text/csv" ||
        selectedFile.name.toLowerCase().endsWith(".csv")
      ) {
        setIsProcessing(true);
        setFile(selectedFile);
        setLearners([]);
        setPreviewData([]);
        setBadRows([]);

        const form = new FormData();
        form.append("csvFile", selectedFile);

        try {
          const response = await previewUsersCsvAsync(form);
          const goodRows = response.goodRows || [];
          const invalidRows = response.badRows || [];

          if (
            !response.success &&
            goodRows.length === 0 &&
            invalidRows.length === 0
          ) {
            setFile(null);
            showToast(
              false,
              "Failed to read CSV file. Check the headers and file format.",
            );
            return;
          }

          setLearners(goodRows);
          setPreviewData(goodRows.slice(0, 5));
          setBadRows(invalidRows);

          if (goodRows.length === 0) {
            showToast(false, "No valid learner data found in CSV");
            return;
          }

          showToast(
            true,
            `Loaded ${goodRows.length} learners from CSV${invalidRows.length ? ` (${invalidRows.length} invalid rows skipped)` : ""}`,
          );
        } catch (error) {
          console.error("Error previewing CSV upload:", error);
          setFile(null);
          setLearners([]);
          setPreviewData([]);
          setBadRows([]);
          showToast(
            false,
            "Failed to read CSV file. Check the headers and file format.",
          );
        } finally {
          setIsProcessing(false);
          e.target.value = null;
        }
      } else {
        showToast(false, "Please upload a CSV file");
        e.target.value = null;
      }
    }
  };

  const handleBulkUpload = async () => {
    if (learners.length === 0) {
      showToast(false, "Please upload a CSV file first");
      return;
    }

    try {
      setIsProcessing(true);
      const data = {
        clientId: clientId,
        learners: learners,
        ...(groupId && { groupId: groupId }), // Only include if specified
      };

      const response = await bulkCreateLearnersAsync(data);

      if (response.success) {
        showToast(true, `Successfully created ${learners.length} learners`);
        // Reset state
        setFile(null);
        setLearners([]);
        setPreviewData([]);
        setBadRows([]);
        setGroupId("");
        if (onSuccess) onSuccess();
      } else {
        showToast(
          false,
          response.errors?.[0]?.error || "Failed to upload learners",
        );
      }
    } catch (error) {
      console.error("Error bulk uploading learners:", error);
      showToast(false, "Failed to upload learners");
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "email,firstName,lastName\nuser@example.com,John,Doe\n";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "learners_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const modalBody = (
    <div style={{ padding: "20px 0" }}>
      {/* Instructions */}
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px",
          borderRadius: "12px",
          marginBottom: "24px",
          color: "white",
        }}
      >
        <h5
          style={{
            marginTop: 0,
            marginBottom: "12px",
            fontSize: "1.1rem",
            fontWeight: "600",
          }}
        >
          📋 Upload Instructions
        </h5>
        <ul
          style={{
            marginBottom: "12px",
            paddingLeft: "20px",
            lineHeight: "1.6",
          }}
        >
          <li>
            Upload a CSV file with columns for email, first name, and last name
          </li>
          <li>First row should contain headers</li>
          <li>All fields are required for each learner</li>
          <li>Invalid or duplicate rows will be skipped before upload</li>
        </ul>
        <button
          onClick={downloadTemplate}
          style={{
            background: "rgba(255,255,255,0.2)",
            color: "white",
            border: "2px solid white",
            borderRadius: "8px",
            padding: "10px 20px",
            fontSize: "0.9rem",
            fontWeight: "600",
            cursor: "pointer",
            transition: "all 0.3s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.color = "#667eea";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "rgba(255,255,255,0.2)";
            e.currentTarget.style.color = "white";
          }}
        >
          📥 Download Template
        </button>
      </div>

      {/* Group Selection */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            fontWeight: "600",
            marginBottom: "10px",
            display: "block",
            color: "#1e293b",
            fontSize: "0.95rem",
          }}
        >
          Group (Optional)
        </label>
        <select
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          style={{
            width: "100%",
            height: "48px",
            fontSize: "1rem",
            borderRadius: "10px",
            border: "2px solid #e2e8f0",
            padding: "0 16px",
            outline: "none",
            backgroundColor: "white",
            cursor: "pointer",
          }}
        >
          <option value="">Auto-assign to default group</option>
          {groups &&
            groups.map((group) => (
              <option key={group.id} value={group.id}>
                {group.name}
              </option>
            ))}
        </select>
        <div
          style={{
            marginTop: "8px",
            fontSize: "0.85rem",
            color: "#64748b",
            fontStyle: "italic",
          }}
        >
          All learners will be assigned to this group (or default group if not
          specified)
        </div>
      </div>

      {/* File Upload */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            fontWeight: "600",
            marginBottom: "10px",
            display: "block",
            color: "#1e293b",
            fontSize: "0.95rem",
          }}
        >
          Select CSV File
        </label>
        <input
          type="file"
          accept=".csv,text/csv"
          onChange={handleFileChange}
          style={{
            width: "100%",
            padding: "12px",
            fontSize: "1rem",
            borderRadius: "10px",
            border: "2px dashed #e2e8f0",
            outline: "none",
            cursor: "pointer",
          }}
        />
        {file && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px",
              background: "#f0fdf4",
              borderRadius: "8px",
              color: "#166534",
              fontWeight: "600",
            }}
          >
            ✓ {file.name} ({learners.length} valid learners
            {badRows.length ? `, ${badRows.length} invalid rows` : ""})
          </div>
        )}
      </div>

      {/* Preview */}
      {previewData.length > 0 && (
        <div>
          <label
            style={{
              fontWeight: "600",
              marginBottom: "12px",
              display: "block",
              color: "#1e293b",
              fontSize: "0.95rem",
            }}
          >
            Preview (First 5 rows)
          </label>
          <div
            style={{
              border: "2px solid #e2e8f0",
              borderRadius: "10px",
              overflow: "hidden",
            }}
          >
            <table style={{ width: "100%", fontSize: "0.85rem" }}>
              <thead style={{ background: "#f8fafc" }}>
                <tr>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    Email
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    First Name
                  </th>
                  <th
                    style={{
                      padding: "12px",
                      textAlign: "left",
                      borderBottom: "2px solid #e2e8f0",
                    }}
                  >
                    Last Name
                  </th>
                </tr>
              </thead>
              <tbody>
                {previewData.map((learner, index) => (
                  <tr
                    key={index}
                    style={{
                      background: index % 2 === 0 ? "white" : "#f8fafc",
                    }}
                  >
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {learner.email}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {learner.firstName}
                    </td>
                    <td
                      style={{
                        padding: "12px",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      {learner.lastName}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {learners.length > 5 && (
            <div
              style={{
                marginTop: "8px",
                fontSize: "0.85rem",
                color: "#64748b",
                fontStyle: "italic",
              }}
            >
              ... and {learners.length - 5} more learners
            </div>
          )}
          {badRows.length > 0 && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px",
                background: "#fff7ed",
                borderRadius: "8px",
                color: "#9a3412",
                fontWeight: "600",
              }}
            >
              {badRows.length} rows were skipped because they were invalid,
              duplicated in the file, or already exist.
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      modalId="bulkUploadModal"
      title="Bulk Upload Learners"
      bodyText={modalBody}
      buttonText={
        isProcessing ? "Uploading..." : `Upload ${learners.length} Learners`
      }
      buttonFunc={handleBulkUpload}
    />
  );
};

export default BulkUploadUsers;
